import React, { useState, useEffect, useCallback } from 'react';

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  DatePicker,
  Switch,
  Popconfirm,
  message,
  Tooltip
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import ListPageHeader from '@shared/ListPageHeader';

import { holidayService } from '@services/operator/holidayService';
import { formatDate } from '@utils/dateTime';

const HolidaysList = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await holidayService.fetchList();
      if (response.success) {
        setData(response.data);
      } else {
        message.error(response.message || t('common.error.fetch_failed'));
      }
    } catch (error) {
      console.error('Fetch holidays error:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      date: dayjs(record.date),
      recurring: record.recurring,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await holidayService.delete(id);
      if (response.success) {
        message.success(t('common.messages.delete_success'));
        fetchList();
      } else {
        message.error(response.message || t('common.error.delete_failed'));
      }
    } catch (error) {
      console.error('Delete holiday error:', error);
      message.error(t('common.error.delete_failed'));
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        date: values.date.format('YYYY-MM-DD'),
      };

      let response;
      if (editingId) {
        response = await holidayService.update(editingId, payload);
      } else {
        response = await holidayService.create(payload);
      }

      if (response.success) {
        message.success(
          editingId
            ? t('common.messages.update_success')
            : t('common.messages.create_success')
        );
        setIsModalVisible(false);
        fetchList();
      } else {
        message.error(response.message || (editingId ? t('common.error.update_failed') : t('common.error.create_failed')));
      }
    } catch (error) {
      console.error('Save holiday error:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: t('models.holiday.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('models.holiday.date'),
      dataIndex: 'date',
      key: 'date',
      render: (text) => formatDate(text, 'YYYY-MM-DD'),
    },
    {
      title: t('models.holiday.recurring'),
      dataIndex: 'recurring',
      key: 'recurring',
      render: (recurring) => recurring ? t('common.yes') : t('common.no'),
    },
    {
      title: t('common.table.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title={t('common.button.edit')}>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('common.confirm.delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.button.confirm')}
            cancelText={t('common.button.cancel')}
          >
            <Tooltip title={t('common.button.delete')}>
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <ListPageHeader
        items={['org_management', 'org_management.holidays']}
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {t('common.button.create')}
          </Button>
        }
      />
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={false}
        locale={{ emptyText: t('common.no_data') }}
      />

      <Modal
        title={editingId ? t('operator.holidays.form.edit_title') : t('operator.holidays.form.create_title')}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t('models.holiday.name')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="date"
            label={t('models.holiday.date')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <DatePicker style={{ width: '100%' }} placeholder={t('common.placeholder.select_date')} />
          </Form.Item>
          <Form.Item
            name="recurring"
            label={t('models.holiday.recurring')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HolidaysList;
